import { z } from "zod";

export const HUB_SESSION_COOKIE = "hub_session";
export const COOKIE_NAME = "combsol_session";
export const TRUSTED_DEVICE_COOKIE = "hub_device_id";
/** Kurzlebiger Einrichtungs-Token nach Ablauf der MFA-Frist (nur setupMfa/enableMfa). */
export const MFA_SETUP_COOKIE = "hub_mfa_setup";
/** Kurzlebiger MFA-Zwischenschritt nach Microsoft-SSO (nur verifyMfa). */
export const MFA_PENDING_COOKIE = "hub_mfa_pending";

// Authoritative Liste der appClaim-Keys aller in server/index.ts registrierten
// Satellite-Apps. Muss synchron zu SATELLITE_APPS und public.apps.key bleiben.
export const SATELLITE_APP_KEYS = [
  "notes",
  "liquid",
  "money",
  "vertrag",
  "briefing",
  "life",
  "scan",
  "fair-kapa",

  // ga-cases, profil, skillpath stillgelegt am 20.10.2026 (Migration 0057)
  "regelwerke",
  "time",
  "back-office",
  "coorconn-liquid",
  "story",
] as const;
export type SatelliteAppKey = (typeof SATELLITE_APP_KEYS)[number];

export const satelliteAppKeySchema = z.enum(SATELLITE_APP_KEYS);

// =====================================================================
// Rollen (vereinfacht seit Migration 0021)
// =====================================================================
// Es gibt nur noch EINE Rollen-Tabelle: public.app_roles.
// Die Rolle bestimmt welche Apps ein User sehen darf (via role_app_permissions).
//
// ADMIN_ROLE_NAMES: Rollen die das Admin-Panel sehen duerfen.
// =====================================================================

export const ROLES = {
  GF: "GF",
  Admin: "Admin",
  OPM_COMBSOL: "OPM-Combsol",
  OPM_COORCONN: "OPM-CoorConn",
  MA: "MA",
  /** Projektassistenz: darf ausschliesslich BackOffice oeffnen (Migration 0052). */
  MITARBEITER_PA: "Mitarbeiter-PA",
  /** Pflegt nur Kontaktdaten interner Mitarbeiter (Migration 0045). */
  VERWALTUNG: "Verwaltung",
  /** Testnutzer (E2E): sieht alle Apps, schreibt nirgends (Migration 0046). */
  TEST: "Test",
  /** Private Systemrolle der Core-Instanz (Migration 0059). */
  CORE_OWNER: "CORE-OWNER",
  /** Private Mitgliedsrolle der Core-Instanz (Migration 0059). */
  CORE_MEMBER: "CORE-MEMBER",
} as const;
export const ROLE_NAMES = [ROLES.GF, ROLES.Admin, ROLES.OPM_COMBSOL, ROLES.OPM_COORCONN, ROLES.MA, ROLES.MITARBEITER_PA, ROLES.VERWALTUNG, ROLES.TEST] as const;
export type RoleName = (typeof ROLE_NAMES)[number];

/** Core-Systemrollen haben einen eigenen DB-Scope und sind nie Firmenrollen. */
export const CORE_ROLE_NAMES = [ROLES.CORE_OWNER, ROLES.CORE_MEMBER] as const;
export const SYSTEM_ROLE_NAMES = [...ROLE_NAMES, ...CORE_ROLE_NAMES] as const;
export type SystemRoleName = (typeof SYSTEM_ROLE_NAMES)[number];

export const roleSchema = z.enum(SYSTEM_ROLE_NAMES);

// Privileged-Set: GF + Admin duerfen das Admin-Panel sehen.
export const ADMIN_ROLE_NAMES = [ROLES.GF, ROLES.Admin] as const;

// MFA-Pflicht (Entscheidung Bjoern 25.09.2026, Frist 30 Tage seit Runde 7):
// GF und Admin im Firmen-Hub muessen TOTP eingerichtet haben. Core-Administratoren
// werden separat und DB-frisch in core_person_settings geprüft. Ohne MFA gilt je Nutzer eine Frist von
// MFA_GRACE_DAYS Tagen ab erstem Login nach Deploy bzw. ab Rollenwechsel
// (users.mfa_frist_bis, Migration 0047). Danach kein Session-Cookie mehr,
// nur ein Einrichtungs-Token fuer setupMfa/enableMfa.
export const MFA_REQUIRED_ROLE_NAMES = [ROLES.GF, ROLES.Admin] as const;
export const MFA_GRACE_DAYS = 30;

export const AUDIT_ACTIONS = [
  "LOGIN", "LOGOUT", "CREATE", "UPDATE", "DELETE",
  "ADMIN_ACTION", "IMPORT", "EXPORT",
  "LOGIN_FAILED", "LOGIN_BLOCKED", "MFA_FAILED", "MFA_SUCCESS",
  "PASSWORD_CHANGED", "SESSION_EXPIRED", "SESSION_HIJACK_SUSPECTED",
  "ACCESS_DENIED", "PRIVILEGE_ESCALATION_ATTEMPT", "ROLE_CHANGED",
  "RATE_LIMIT_EXCEEDED", "BRUTE_FORCE_DETECTED", "SUSPICIOUS_REQUEST",
  "INVALID_INPUT", "UNAUTHORIZED_API_CALL", "MALFORMED_REQUEST",
  "SENSITIVE_DATA_ACCESSED", "DATA_EXPORTED", "BULK_DATA_ACCESS",
  "USER_CREATED", "USER_DELETED", "USER_DISABLED",
  "PERMISSION_CHANGED", "SECURITY_SETTING_CHANGED",
  "TRUSTED_DEVICE_CREATED", "TRUSTED_DEVICE_USED",
  "TRUSTED_DEVICE_REVOKED", "TRUSTED_DEVICE_EXPIRED",
  // MFA-Pflicht GF/Admin (Migration 0047)
  "MFA_GRACE_STARTED", "MFA_GRACE_LOGIN", "MFA_SETUP_REQUIRED",
  // Feste Verknuepfung Nutzer zu Person (Migration 0049), nur GF
  "PERSON_LINKED", "PERSON_UNLINKED",
  // Individuelle Rollen (Migration 0053), nur GF
  "ROLE_CREATED",
  "ROLE_LABEL_UPDATED", "ROLE_ARCHIVED", "ROLE_RESTORED", "ROLE_DELETED",
  // Core-Personenmodell (Migration 0060): keine Namen, E-Mails oder
  // fachlichen Daten in den Details; nur Sicherheits- und Paritätsmetadaten.
  "CORE_PERSONAL_CUTOVER", "CORE_PERSON_CREATED", "CORE_PERSON_ADMIN_CHANGED",
  "CORE_PERSON_APP_CHANGED", "CORE_PERSON_STATUS_CHANGED",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const SEVERITY_LEVELS = ["info", "warning", "error", "critical"] as const;
export type Severity = (typeof SEVERITY_LEVELS)[number];

export const ENTITY_TYPES = [
  "user", "mitarbeiter", "kunde", "projekt", "monitor_app", "session", "person_data",
  "ki_auftrag", // KI-Auftragszentrale (Migration 0048), Bereich entfernt, Altdaten bleiben lesbar
  "role", // Rollen und Rolle-mal-App-Matrix (Migration 0053)
  "service_identity", // Dienstidentitaeten, z. B. nexus (Migration 0055)
  "core_person", // getrennte Core-Personen-/Appsteuerung (Migration 0060)
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

// Kanonische Liste aller Hub-Apps fuer Berechtigungssteuerung
export const HUB_APPS = [
  { id: "notes", label: "Nexus" },
  { id: "liquid", label: "Liquid" },
  { id: "vertrag", label: "Vertrag" },
  { id: "briefing", label: "Briefing" },
  { id: "life", label: "Life" },
  { id: "scan", label: "Scan" },
  { id: "fair-kapa", label: "FAIR-Kapa" },

  { id: "regelwerke", label: "Regelwerke" },
  { id: "time", label: "Time" },
  { id: "back-office", label: "BackOffice" },
  { id: "coorconn-liquid", label: "CoorConn Liquid" },
  { id: "story", label: "Story" },
] as const;
export type HubAppId = (typeof HUB_APPS)[number]["id"];

export const STATUS_VALUES = ["aktiv", "inaktiv", "geloescht"] as const;
export type StatusValue = (typeof STATUS_VALUES)[number];

/** Anmeldeereignisse im audit_log (Filter "Anmeldungen" im Audit, frueher System Logs). */
export const AUTH_EVENT_ACTIONS = [
  "LOGIN", "LOGOUT", "LOGIN_FAILED", "LOGIN_BLOCKED",
  "MFA_FAILED", "MFA_SUCCESS", "PASSWORD_CHANGED",
  "SESSION_EXPIRED", "SESSION_HIJACK_SUSPECTED",
  "ACCESS_DENIED", "ROLE_CHANGED",
] as const;
